/**
 * @fileoverview Complex visit close-out contract — the questions asked when the anchor inspection
 *               closes, and the pure planner that decides which governed buildings the system may
 *               complete as passing and which the inspector must open by hand.
 * @module import-core/contracts/complex-inspections/complexVisitCloseout
 *
 * Responsibilities:
 * - Define the close-out question and answer shapes (`complexCloseoutQuestionSchema`,
 *   `complexCloseoutAnswerSchema`) and the built-in default question set.
 * - Plan the close-out (`planComplexCloseout`): hold out buildings named in a gating answer, list the
 *   remaining incomplete governed buildings for system completion, and report every blocking issue.
 *
 * Dependencies:
 * - `zod`; `ComplexVisitProgressParticipant` from `./complexVisitFinalization` (type only).
 *
 * Architecture:
 * - Pure functions only. Mobile and tenant hosts call the planner; the host completes the buildings.
 *   Tenant-managed question lists are a later layer — hosts pass `questions` in, defaulting to
 *   `DEFAULT_COMPLEX_CLOSEOUT_QUESTIONS`.
 *
 * Notes:
 * - Standalone participants are never planned: they follow the normal inspection path and are
 *   not part of the complex close-out.
 * - Fails closed: an unresolved governed participant (status null) is an issue and is never auto-passed.
 * - An in-progress governed building is never auto-passed: it already has inspector-entered answers,
 *   so it is held out for manual completion instead of being overwritten with passing defaults.
 */

import { z } from 'zod';
import type { ComplexVisitProgressParticipant } from './complexVisitFinalization';

const TERMINAL_STATUSES = new Set(['completed', 'closedOut']);

/**
 * @contract complexCloseoutQuestionSchema
 * Purpose: One yes/no close-out question that may name the buildings it applies to.
 * Fields: code (stable key), label (display text), followUp (what a Yes feeds downstream),
 *   holdsOutBuildings (named buildings must be opened by hand instead of auto-passed).
 * Rules: Strict; a Yes answer must name at least one building.
 * Sync: Hosts supply the list; defaults below.
 */
export const complexCloseoutQuestionSchema = z
  .object({
    code: z.string().min(1),
    label: z.string().min(1),
    followUp: z.enum(['permit', 'violation', 'reinspection', 'none']),
    holdsOutBuildings: z.boolean(),
  })
  .strict();
export type ComplexCloseoutQuestion = z.infer<typeof complexCloseoutQuestionSchema>;

/**
 * @contract complexCloseoutAnswerSchema
 * Purpose: The inspector's answer to one close-out question, stored on the root event.
 * Fields: questionCode, answer, entityIds (buildings the Yes applies to; empty for No).
 * Rules: Strict.
 */
export const complexCloseoutAnswerSchema = z
  .object({
    questionCode: z.string().min(1),
    answer: z.boolean(),
    entityIds: z.array(z.string()),
  })
  .strict();
export type ComplexCloseoutAnswer = z.infer<typeof complexCloseoutAnswerSchema>;

/** Built-in default close-out questions; tenants may replace the list. */
export const DEFAULT_COMPLEX_CLOSEOUT_QUESTIONS: readonly ComplexCloseoutQuestion[] = [
  {
    code: 'permitsToIssue',
    label: 'Are there permits to be issued to any of the buildings?',
    followUp: 'permit',
    holdsOutBuildings: true,
  },
  {
    code: 'concernsInUnopened',
    label: 'Did you observe violations or hazards in any building you did not open?',
    followUp: 'violation',
    holdsOutBuildings: true,
  },
  {
    code: 'needsReinspection',
    label: 'Should any building be scheduled for re-inspection?',
    followUp: 'reinspection',
    holdsOutBuildings: false,
  },
];

/** @contract COMPLEX_CLOSEOUT_QUESTION_LOOKUP_TYPE — lookup category that holds the tenant-managed question list. */
export const COMPLEX_CLOSEOUT_QUESTION_LOOKUP_TYPE = 'complexCloseoutQuestion';

/** @contract ComplexCloseoutQuestionLookupRow — the lookup-value fields the resolver reads. */
export interface ComplexCloseoutQuestionLookupRow {
  code: string;
  name: string;
  sortOrder?: number;
  isActive?: boolean;
  _deleted?: boolean;
  config?: Record<string, unknown>;
}

const FOLLOW_UPS = new Set(['permit', 'violation', 'reinspection', 'none']);

/**
 * @process resolveComplexCloseoutQuestions
 * Steps: drop inactive, deleted, or incomplete rows and duplicate codes; order by sortOrder then code; read
 *   `config.followUp` (permit | violation | reinspection | none) and `config.holdsOutBuildings` (true only when
 *   exactly true); fall back to the supplied defaults when nothing usable remains.
 * Inputs: lookup rows of type `complexCloseoutQuestion` and the fallback list.
 * Outputs: the question list the wizard and planner use.
 * Failure Handling: a row with missing or unknown config is informational (followUp none, holds out nothing) —
 *   it never gates a building by accident. An empty list falls back to the defaults, so hiding every question
 *   cannot silently skip the permit and concern questions.
 * Idempotency: REQUIRED — pure function.
 */
export function resolveComplexCloseoutQuestions(
  rows: readonly ComplexCloseoutQuestionLookupRow[],
  fallback: readonly ComplexCloseoutQuestion[] = DEFAULT_COMPLEX_CLOSEOUT_QUESTIONS,
): ComplexCloseoutQuestion[] {
  const seen = new Set<string>();
  const questions: ComplexCloseoutQuestion[] = [];
  const ordered = rows
    .filter((row) => row.isActive !== false && row._deleted !== true && row.code && row.name)
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.code.localeCompare(b.code));
  for (const row of ordered) {
    if (seen.has(row.code)) continue;
    seen.add(row.code);
    const followUp = typeof row.config?.followUp === 'string' && FOLLOW_UPS.has(row.config.followUp)
      ? (row.config.followUp as ComplexCloseoutQuestion['followUp'])
      : 'none';
    questions.push({ code: row.code, label: row.name, followUp, holdsOutBuildings: row.config?.holdsOutBuildings === true });
  }
  return questions.length > 0 ? questions : [...fallback];
}

/** @contract ComplexCloseoutIssueCode — reasons a close-out cannot proceed. */
export type ComplexCloseoutIssueCode =
  | 'UNANSWERED_QUESTION'
  | 'UNKNOWN_QUESTION'
  | 'DUPLICATE_ANSWER'
  | 'ANSWER_NEEDS_BUILDINGS'
  | 'UNEXPECTED_BUILDINGS'
  | 'UNKNOWN_BUILDING'
  | 'PARTICIPANT_UNRESOLVED'
  | 'PARTICIPANT_IN_PROGRESS'
  | 'HELD_OUT_NOT_COMPLETE';

/** @contract ComplexCloseoutIssue — one blocking reason, with the question/building it concerns. */
export interface ComplexCloseoutIssue {
  code: ComplexCloseoutIssueCode;
  questionCode?: string;
  entityId?: string;
}

/** @contract ComplexCloseoutBuilding — a governed building the planner placed in a group. */
export interface ComplexCloseoutBuilding {
  entityId: string;
  caseId: string;
  eventIndex: number;
  entityName: string;
  status: string | null;
  /** Question codes that named this building (held-out buildings only). */
  questionCodes: string[];
}

/**
 * @contract ComplexCloseoutPlan
 * Purpose: Planner output the host renders and acts on.
 * Fields: canProceed (anchor may close), issues, heldOut (must be opened by hand),
 *   autoPass (system may complete as passing), and the building ids each follow-up names.
 * Rules: canProceed is true only with no issues and every held-out building already terminal.
 */
export interface ComplexCloseoutPlan {
  canProceed: boolean;
  issues: ComplexCloseoutIssue[];
  heldOut: ComplexCloseoutBuilding[];
  autoPass: ComplexCloseoutBuilding[];
  permitEntityIds: string[];
  violationEntityIds: string[];
  reinspectionEntityIds: string[];
}

/**
 * @process planComplexCloseout
 * Steps: validate answers against the questions; hold out the governed buildings named by Yes answers on
 *   gating questions; flag held-out buildings that are not yet complete; list every other incomplete
 *   governed building for system completion; collect the building ids each follow-up names.
 * Inputs: questions, answers, and the visit's participants (from `deriveComplexVisitProgress`).
 * Outputs: ComplexCloseoutPlan.
 * Failure Handling: never throws; every problem becomes an issue. Unresolved participants fail closed.
 * Idempotency: REQUIRED — pure function.
 */
export function planComplexCloseout(input: {
  questions: readonly ComplexCloseoutQuestion[];
  answers: readonly ComplexCloseoutAnswer[];
  participants: readonly ComplexVisitProgressParticipant[];
}): ComplexCloseoutPlan {
  const issues: ComplexCloseoutIssue[] = [];
  const governed = input.participants.filter((p) => p.participationMode === 'complex-governed');
  const governedById = new Map(governed.map((p) => [p.entityId, p]));
  const questionsByCode = new Map(input.questions.map((q) => [q.code, q]));

  const answersByCode = new Map<string, ComplexCloseoutAnswer>();
  for (const answer of input.answers) {
    if (!questionsByCode.has(answer.questionCode)) {
      issues.push({ code: 'UNKNOWN_QUESTION', questionCode: answer.questionCode });
    } else if (answersByCode.has(answer.questionCode)) {
      issues.push({ code: 'DUPLICATE_ANSWER', questionCode: answer.questionCode });
    } else {
      answersByCode.set(answer.questionCode, answer);
    }
  }

  const heldOutReasons = new Map<string, string[]>();
  const followUpIds: Record<'permit' | 'violation' | 'reinspection', Set<string>> = {
    permit: new Set(), violation: new Set(), reinspection: new Set(),
  };
  for (const question of input.questions) {
    const answer = answersByCode.get(question.code);
    if (!answer) {
      issues.push({ code: 'UNANSWERED_QUESTION', questionCode: question.code });
      continue;
    }
    if (!answer.answer) {
      if (answer.entityIds.length > 0) issues.push({ code: 'UNEXPECTED_BUILDINGS', questionCode: question.code });
      continue;
    }
    if (answer.entityIds.length === 0) {
      issues.push({ code: 'ANSWER_NEEDS_BUILDINGS', questionCode: question.code });
      continue;
    }
    for (const entityId of new Set(answer.entityIds)) {
      if (!governedById.has(entityId)) {
        issues.push({ code: 'UNKNOWN_BUILDING', questionCode: question.code, entityId });
        continue;
      }
      if (question.followUp !== 'none') followUpIds[question.followUp].add(entityId);
      if (question.holdsOutBuildings) {
        heldOutReasons.set(entityId, [...(heldOutReasons.get(entityId) ?? []), question.code]);
      }
    }
  }

  const toBuilding = (p: ComplexVisitProgressParticipant, questionCodes: string[] = []): ComplexCloseoutBuilding => ({
    entityId: p.entityId,
    caseId: p.caseId,
    eventIndex: p.eventIndex,
    entityName: p.entityName,
    status: p.status,
    questionCodes,
  });

  const heldOut: ComplexCloseoutBuilding[] = [];
  const autoPass: ComplexCloseoutBuilding[] = [];
  for (const participant of governed) {
    const reasons = heldOutReasons.get(participant.entityId);
    if (reasons) {
      heldOut.push(toBuilding(participant, reasons));
      if (participant.status === null || !TERMINAL_STATUSES.has(participant.status)) {
        issues.push({ code: 'HELD_OUT_NOT_COMPLETE', entityId: participant.entityId });
      }
      continue;
    }
    if (participant.status === null) {
      issues.push({ code: 'PARTICIPANT_UNRESOLVED', entityId: participant.entityId });
    } else if (participant.status === 'inProgress') {
      // Already opened by the inspector: its draft answers must not be overwritten by passing
      // defaults. Hold it out for manual completion like a gating-answer holdout.
      heldOut.push(toBuilding(participant));
      issues.push({ code: 'PARTICIPANT_IN_PROGRESS', entityId: participant.entityId });
    } else if (!TERMINAL_STATUSES.has(participant.status)) {
      autoPass.push(toBuilding(participant));
    }
  }

  return {
    canProceed: issues.length === 0,
    issues,
    heldOut,
    autoPass,
    permitEntityIds: [...followUpIds.permit],
    violationEntityIds: [...followUpIds.violation],
    reinspectionEntityIds: [...followUpIds.reinspection],
  };
}
